/**
 * Inline bootstrap script that recovers from a Next.js hydration deadlock caused by
 * CDN/buildId asset skew during a deploy.
 *
 * Failure mode it guards against: production serves JS from a separate CDN
 * (`static.useposeidon.com`) via `assetPrefix`. During a deploy the new Cloud Run revision
 * serves HTML referencing a new `buildId` before that build's `_next/static/<buildId>/_buildManifest.js`
 * is present/propagated on the CDN (and the previous build's assets are gone). A 404 on that
 * manifest makes the Next.js Pages Router router init await `_buildManifest.js` forever, so the
 * client never hydrates — users are stuck on the server-rendered loading shell with no React
 * effects, no auth, and no requests, until the CDN's negative cache expires.
 *
 * This script runs independent of hydration. If the app hasn't hydrated after a timeout (or a
 * `_next/static` asset fails to load), it performs a single, sessionStorage-guarded hard reload
 * to recover (the CDN has usually caught up by then). A second consecutive failure does NOT
 * reload again, to avoid a reload loop — the user is left on the page rather than thrashing.
 *
 * Why inline: the recovery script must come from the app origin (Cloud Run), never the CDN it is
 * meant to recover from, and must survive the strict production CSP (no 'unsafe-inline'). It is
 * emitted verbatim in `_document`, and `next.config.ts` hashes this exact constant into the CSP
 * `script-src` so the hash can never drift from the script. `_app` sets `window.__APP_HYDRATED`
 * on mount and clears the guard key — see [[_app]] and the `script-src` block in next.config.ts.
 *
 * Keep this a single self-contained IIFE using only widely-supported browser APIs (no const/let
 * is required, but they are fine). Do not include the literal `</script>` sequence.
 */
export const HYDRATION_RELOAD_KEY = 'poseidon_hydration_reloaded';

/** Time to wait for hydration before assuming a deadlock and reloading once. */
const HYDRATION_TIMEOUT_MS = 12000;

export const HYDRATION_WATCHDOG_SCRIPT = `(function(){
  if(location.hostname==="localhost"||location.hostname==="127.0.0.1")return;
  var KEY="${HYDRATION_RELOAD_KEY}",TIMEOUT=${HYDRATION_TIMEOUT_MS};
  function notHydrated(){return !window.__APP_HYDRATED;}
  function recover(){
    if(!notHydrated())return;
    var already=false;
    try{already=sessionStorage.getItem(KEY)==="1";}catch(e){}
    if(already)return;
    try{sessionStorage.setItem(KEY,"1");}catch(e){}
    location.reload();
  }
  window.addEventListener("error",function(e){
    var t=e&&e.target;
    if(t&&t.tagName==="SCRIPT"&&t.src&&String(t.src).indexOf("/_next/static/")!==-1)recover();
  },true);
  setTimeout(recover,TIMEOUT);
})();`;
